import { jsPDF } from 'jspdf';
import {
  CalendarTaskHeaderGroup,
  CommonUtils,
  Roboto,
} from '@infodata-group/inf-commons';
import { CalendarHoursComponent } from '../calendar-hours/calendar-hours.component';
import { QueryList } from '@angular/core';
import { CalendarView } from '../calendar-view';
import { PdfHelper } from './pdf-helper';
import html2canvas from 'html2canvas';
import { CalendarHorizontalResourcesComponent } from '../calendar-horizontal-resources/calendar-horizontal-resources.component';

export class PlanningToPdf {
  public doc: jsPDF;

  // Component
  private hoursComponent: CalendarHoursComponent;
  private resourcesComponent: CalendarHorizontalResourcesComponent;

  constructor(orientation: 'p' | 'l' = 'l', format: 'a4' | 'a3' | 'a2' = 'a3') {
    this.doc = new jsPDF(orientation, 'pt', format); // A3 size page of PDF
    this.doc.addFileToVFS('Roboto-Regular.ttf', Roboto.regular);
    this.doc.addFileToVFS('Roboto-Bold.ttf', Roboto.bold);
    this.doc.addFont('Roboto-Regular.ttf', 'roboto', 'normal');
    this.doc.addFont('Roboto-Bold.ttf', 'roboto', 'bold');
    this.doc.setFont('roboto');
    this.doc.setFontSize(12);
    this.doc.setLineWidth(0.5);
  }

  async setHtml(container: HTMLElement) {
    const cloned = container.cloneNode(true) as HTMLElement;
    const scrollWidth = container.scrollWidth;
    const scrollHeight = container.scrollHeight;
    cloned.style.width = scrollWidth + 'px';
    cloned.style.height = scrollHeight + 'px';
    cloned.style.overflow = 'visible';
    cloned.style.left = '-9999px';
    cloned.style.top = '0';
    cloned.style.zIndex = '-1';
    cloned.style.background = '#fff';
    document.body.appendChild(cloned);
    await new Promise((resolve) => setTimeout(resolve, 100));
    const canvas = await html2canvas(cloned, {
      useCORS: true,
      backgroundColor: CommonUtils.color('white'),
      scale: 2,
    });
    document.body.removeChild(cloned);
    const imgData = canvas.toDataURL('image/png');
    const pageWidth = this.doc.internal.pageSize.getWidth();
    const pageHeight = this.doc.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let position = 0;
    while (position < imgHeight) {
      this.doc.addImage(imgData, 'PNG', 0, -position, imgWidth, imgHeight);
      position += pageHeight;
      if (position < imgHeight) this.doc.addPage();
    }
    return this.doc;
  }

  addHours(hoursComponent: CalendarHoursComponent) {
    this.hoursComponent = hoursComponent;
    this.doc.setFontSize(11);
    hoursComponent.generatePDF(this.doc);
    this.doc.setDrawColor(CommonUtils.color('black'));
    this.doc.setLineDashPattern(null, 0);
    this.doc.setFontSize(13);
  }

  generateHeaders(groups: CalendarTaskHeaderGroup[], resPerPage: number) {
    let x = this.hoursComponent ? 40 : 10;
    this.doc.setFontSize(12);
    const margin = x + 10;
    const width = this.doc.internal.pageSize.width - margin;
    const resLength = groups.reduce(
      (n, team) => (n += team.tasksGroups.length),
      0,
    );
    let columnWidth =
      width / (resLength <= resPerPage ? resLength : resPerPage);

    let index = 0;
    let count = 0;
    for (const group of groups) {
      let y = 10;
      let groupIndex = 0;
      const pageRes = resPerPage - index;

      const groupWidth =
        group.tasksGroups.length > pageRes ? pageRes : group.tasksGroups.length;
      // Group header
      this.doc.rect(x, y, columnWidth * groupWidth, 30);
      this.doc.text(
        PdfHelper.generateTextLine(
          this.doc,
          group.id,
          groupWidth * columnWidth,
          30,
        ),
        x + 10,
        y + 20,
      );
      y += 30;
      // Resource header
      for (const res of group.tasksGroups) {
        this.doc.rect(x, y, columnWidth, 30);
        this.doc.text(
          PdfHelper.generateTextLine(
            this.doc,
            res.toString(),
            columnWidth - 10,
            30,
          ),
          x + 5,
          y + 20,
        );
        x += columnWidth;
        ++index;
        ++count;
        ++groupIndex;
        if (index >= resPerPage) {
          this.doc.addPage();
          if (this.hoursComponent) {
            this.addHours(this.hoursComponent);
          }
          x = 40;
          index = 0;
          const rest = resLength - count;
          columnWidth = width / (rest <= resPerPage ? rest : resPerPage);
          if (groupIndex < group.tasksGroups.length) {
            this.insertGroup(groupIndex, columnWidth, group, resPerPage);
          }
        }
      }
    }
  }

  generate(views: QueryList<CalendarView>, resPerPage: number) {
    this.doc.setPage(1);
    let x = this.hoursComponent ? 40 : 10;
    const margin = x + 10;
    let index = 0;
    let count = 0;
    let page = 1;
    let width =
      (this.doc.internal.pageSize.width - margin) /
      (views.length > resPerPage ? resPerPage : views.length);
    for (const v of views.toArray()) {
      v.toPdf(this.doc, width, x);
      x += width;
      ++index;
      ++count;
      if (index >= resPerPage && count < views.toArray().length) {
        ++page;
        index = 0;
        this.doc.setPage(page);
        width =
          (this.doc.internal.pageSize.width - margin) /
          (views.length - count > resPerPage
            ? resPerPage
            : views.length - count);
        x = this.hoursComponent ? 40 : 10;
      }
    }
  }

  private insertGroup(
    groupIndex: number,
    columnWidth: number,
    group: CalendarTaskHeaderGroup,
    resPerPage: number,
  ) {
    let groupWidth = group.tasksGroups.length - groupIndex;
    groupWidth = groupWidth < resPerPage ? groupWidth : resPerPage;
    this.doc.rect(40, 10, columnWidth * groupWidth, 30);
    this.doc.text(
      PdfHelper.generateTextLine(
        this.doc,
        group.id,
        groupWidth * columnWidth,
        30,
      ),
      50,
      30,
    );
  }

  generateHorizontal(currentView: CalendarView) {
    currentView.toPdf(this.doc, this.doc.internal.pageSize.width, 0);
  }
}
